import React, {FC} from 'react';
import styled from 'styled-components';

import {useAsyncTableSource} from '../../../../../../../../src/components/common/table/helpers/useAsyncTableSource';
import {TableRowRenderOptions} from '../../../../../../../../src/components/common/table/tableRow';
import {VirtualTable} from '../../../../../../../../src/components/common/table/virtualTable';
import {VirtualTableRowRenderer} from '../../../../../../../../src/components/common/table/virtualTableBase';
import {buildContactsManagerCardGroupContactsUrl} from '../../../../../../../../src/helpers/contactsManagerHelpers';
import {TableTokenPaginationDataModel} from '../../../../../../../../src/models/table/tablePaginationDataModel';
import {ContactsManagerTableStatusesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {Handler, HandlerOf, Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../../../shared/helpers/src/utilities/asyncCancelHelpers';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {cardGroupsTableColumns} from '../common/cardGroups/cardGroupsTableConfig';
import {CardGroupsTableRowContainer} from '../common/cardGroups/cardGroupsTableRowContainer';

/*
 * Props.
 */

interface CardGroupsSearchTableProps {
  cardGroupIds: ReadonlyArray<number>;
  tableStatus?: ContactsManagerTableStatusesEnum;
  paginationData?: TableTokenPaginationDataModel;
  query: string;
  onFetchTableDataAsync: (query: string, cancelToken: CancelToken) => Promise<void>;
  renderEmptyState: Renderer;
  onReset: Handler;
  navigateToLink: HandlerOf<string>;
}

/*
 * Styles.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 48px 0 36px;
  position: relative;
`;

/*
 * Component.
 */

export const CardGroupsSearchTable: FC<CardGroupsSearchTableProps> = ({
  cardGroupIds,
  paginationData,
  query,
  onFetchTableDataAsync,
  renderEmptyState,
  onReset,
  navigateToLink,
}) => {
  const source = useAsyncTableSource(
    {
      onReset: async () => onReset(),
      fetcher: async ({cancelToken}) => onFetchTableDataAsync(query, cancelToken),
    },
    [query],
  );

  const renderRow: VirtualTableRowRenderer<number> = (
    cardGroupId: number,
    options: TableRowRenderOptions,
  ) => (
    <CardGroupsTableRowContainer
      isSelected={false}
      cardGroupId={cardGroupId}
      options={options}
      tableColumns={cardGroupsTableColumns}
      onSelect={() => {
        navigateToLink(buildContactsManagerCardGroupContactsUrl(cardGroupId));
      }}
    />
  );

  return (
    <StyledTableContainerDiv>
      <VirtualTable<number>
        columns={cardGroupsTableColumns}
        source={source}
        paginationData={paginationData}
        items={cardGroupIds}
        size={VisualSizesEnum.LARGE}
        renderRow={renderRow}
        renderEmpty={renderEmptyState}
      />
    </StyledTableContainerDiv>
  );
};
